import css from '@emotion/css';
import styled from '@emotion/styled';
import { SerializedStyles } from '@emotion/utils';
import {
  Stepper as MuiStepper,
  Typography,
  createStyles,
  StepConnector,
} from '@material-ui/core';

import { ICustomTheme } from 'interfaces';

type Theme = {
  theme?: ICustomTheme;
};

const goalXsWidth = 150;

export const cssContainer = css({
  position: 'relative',
  width: '100%',
});

export const cssMarginTop = (theme: ICustomTheme): SerializedStyles =>
  css({
    marginTop: theme.spacing(2),
  });

export const cssStep = (theme: ICustomTheme): SerializedStyles =>
  css({
    [theme.breakpoints.down('xs')]: {
      minWidth: goalXsWidth,
      '&:first-child': {
        marginLeft: 'calc(48vw - 102px)', // keeps the first element centered on xs
      },
    },
  });

export const dashedConnectorStyles = createStyles((theme: ICustomTheme) => ({
  line: {
    backgroundImage: `
      radial-gradient(
        ${theme.colors.paletteFromBO.main},
        ${theme.colors.paletteFromBO.main} 5px,
        transparent 5px,
        transparent
      )
    `,
    backgroundSize: '30px 100%',
    border: 0,
    height: 6,
  },
  root: {
    top: 45,
    '&.disabled': {
      '& $line': {
        backgroundImage: `
          radial-gradient(
            ${theme.colors.grey.light},
            ${theme.colors.grey.light} 5px,
            transparent 5px,
            transparent
          )
        `,
      },
    },
    '&.isRewardingAuto': {
      top: 70,
    },
  },
}));

export const stepLabelStyles = createStyles(() => ({
  iconContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingRight: 0,
    width: '100%',
  },
  root: {
    position: 'relative',
    zIndex: 1,
  },
}));

type CustomConnectorProps = {
  disabled?: boolean;
  theme: ICustomTheme;
  top?: number;
};

export const CustomConnector = styled(StepConnector)<CustomConnectorProps>(
  ({ disabled = false, theme, top = 17 }) => ({
    padding: theme.spacing(0, 1.5),
    top,
    '& .MuiStepConnector-line': {
      backgroundImage: `linear-gradient(
      45deg,
      ${theme.palette.grey[200]} 25%,
      ${theme.palette.grey[300]} 25%,
      ${theme.palette.grey[300]} 50%,
      ${theme.palette.grey[200]} 50%,
      ${theme.palette.grey[200]} 75%,
      ${theme.palette.grey[300]} 75%,
      ${theme.palette.grey[300]} 100%
    )`,
      backgroundSize: '16px 16px',
      border: 0,
      height: 16,
      overflow: 'hidden',
    },
    ...(!disabled && {
      '&.MuiStepConnector-active': {
        '& .MuiStepConnector-line': {
          backgroundImage: `linear-gradient(
          45deg,
          ${theme.colors.white} 25%,
          ${theme.colors.paletteFromBO.primary} 25%,
          ${theme.colors.paletteFromBO.primary} 50%,
          ${theme.colors.white} 50%,
          ${theme.colors.white} 75%,
          ${theme.colors.paletteFromBO.primary} 75%,
          ${theme.colors.paletteFromBO.primary} 100%
        )`,
        },
      },
    }),
  }),
);

export const Stepper = styled(MuiStepper)<{
  theme: ICustomTheme;
  nbOfSteps?: number;
}>(({ theme, nbOfSteps = 1 }) => ({
  position: 'relative',
  width: '100%',
  [theme.breakpoints.down('xs')]: {
    padding: `0 calc(${goalXsWidth * nbOfSteps}px + 48vw) 0 0`,
    /* necessary to allow user to scroll past the last step */
  },
  [theme.breakpoints.only('sm')]: {
    paddingBottom: theme.spacing(1),
    paddingTop: 0,
  },
}));

export const Text = styled(Typography)<Theme>(({ theme }) => ({
  color: theme.colors.paletteFromBO.main,
  fontSize: theme.typography.body2.fontSize,
  fontWeight: theme.typography.fontWeightBold,
  '&.disabled': {
    color: theme.colors.grey.light,
  },
  '&.info': {
    color: theme.colors.grey.dark,
  },
  '&.marginBottom': {
    marginBottom: theme.spacing(2),
  },
  '&.next': {
    color: theme.colors.paletteFromBO.primary,
  },
  '&.white': {
    color: theme.colors.white,
  },
}));
